import React from 'react';
import { Heart, Users, Compass, Landmark, Feather } from 'lucide-react';
import { clsx } from 'clsx';
import { getArt } from '../art/artFiles';
import { PORTRAITS } from '../../data/artwork';

const ICONS = {
  philemon: Heart,
  xenia: Users,
  theron: Compass,
  all: Landmark, // the courtyard itself — tools shared by all three guides
  daria: Feather, // the guest learner — a fledgling voice
};

const COLOURS = {
  philemon: {
    bg: 'bg-philemon-bg',
    border: 'border-courtyard-olive',
    text: 'text-courtyard-olive-dark',
    gradient: 'from-courtyard-olive to-courtyard-olive-light',
  },
  xenia: {
    bg: 'bg-xenia-bg',
    border: 'border-courtyard-aegean',
    text: 'text-courtyard-aegean-dark',
    gradient: 'from-courtyard-aegean to-courtyard-aegean-light',
  },
  theron: {
    bg: 'bg-theron-bg',
    border: 'border-courtyard-terracotta',
    text: 'text-courtyard-clay',
    gradient: 'from-courtyard-terracotta to-courtyard-terracotta-light',
  },
  all: {
    bg: 'bg-courtyard-gold/10',
    border: 'border-courtyard-gold',
    text: 'text-courtyard-gold-ink',
    gradient: 'from-courtyard-gold to-courtyard-gold-light',
  },
  daria: {
    bg: 'bg-courtyard-stone/10',
    border: 'border-courtyard-stone',
    text: 'text-courtyard-stone',
    gradient: 'from-courtyard-stone to-courtyard-stone',
  },
};

const CharacterAvatar = ({ characterId, size = 'md', showName = false, className }) => {
  const Icon = ICONS[characterId] || Heart;
  const colours = COLOURS[characterId] || COLOURS.philemon;

  const sizes = {
    xs: { wrapper: 'w-7 h-7', icon: 'w-3.5 h-3.5', text: 'text-[10px]', px: 28 },
    sm: { wrapper: 'w-10 h-10', icon: 'w-5 h-5', text: 'text-xs', px: 40 },
    md: { wrapper: 'w-14 h-14', icon: 'w-7 h-7', text: 'text-sm', px: 56 },
    lg: { wrapper: 'w-20 h-20', icon: 'w-10 h-10', text: 'text-base', px: 80 },
    xl: { wrapper: 'w-28 h-28', icon: 'w-14 h-14', text: 'text-lg', px: 112 },
  };

  const s = sizes[size] || sizes.md;
  const name =
    characterId === 'all'
      ? 'All three'
      : characterId?.charAt(0).toUpperCase() + characterId?.slice(1);

  // The painted portrait, once it exists (data/artwork.js). Until then, and for
  // "all three", the icon. Either way it is decoration: the guide's name is
  // always written beside it, so the picture carries no alt text of its own.
  const portrait = PORTRAITS[characterId] ? getArt(PORTRAITS[characterId].file) : null;

  return (
    <div className={clsx('flex flex-col items-center gap-1', className)}>
      {portrait ? (
        <img
          src={portrait.src}
          srcSet={portrait.srcSet}
          sizes={`${s.px}px`}
          alt=""
          width={s.px}
          height={s.px}
          loading="lazy"
          decoding="async"
          className={clsx(s.wrapper, 'rounded-full border-2 object-cover', colours.border)}
        />
      ) : (
        <div
          className={clsx(
            s.wrapper,
            'rounded-full border-2 flex items-center justify-center',
            'bg-gradient-to-br',
            colours.gradient,
            colours.border
          )}
        >
          <Icon className={clsx(s.icon, 'text-white')} aria-hidden="true" />
        </div>
      )}
      {showName && (
        <span className={clsx(s.text, 'font-medium', colours.text)}>
          {name}
        </span>
      )}
    </div>
  );
};

export default CharacterAvatar;
